/**
 * What Nexus recorded actually happening.
 *
 * Deliberately boring. Everything here is a fact the platform stored; nothing
 * here is an interpretation of why. An operator in the middle of an incident
 * needs to know what happened, and a generated explanation sitting among the
 * evidence would be indistinguishable from evidence.
 *
 * The one piece of judgement the screen does exercise is about **absence**. A
 * result may be short of records the caller may not read, and saying so is
 * important — but what was withheld is never described, because "two
 * safety-restricted records exist under this correlation" is itself the
 * disclosure.
 */

import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import type { AuditRecord, AuditResult } from "../api/types";
import { usePagedCollection } from "../api/useCollection";
import { useResource } from "../api/useResource";
import {
  Banner,
  Card,
  Classification,
  Fact,
  Facts,
  Identifier,
  LowerBound,
  Mono,
  More,
  outcomeTone,
  Pill,
  when,
} from "../components/primitives";
import { Empty, Show } from "../components/states";

/**
 * The page size the search has always asked for.
 *
 * Unchanged on purpose. What changes is not how much is read at once but
 * whether the fifty-first record is reachable at all.
 */
const PAGE = 50;

/**
 * What a result is short of, said without describing what it is short of.
 *
 * `where` is the whole reason this takes props rather than a result. A
 * correlation is one window and its count is a fact about that window. The
 * search is a walk, and after two pages the honest subject is the pages that
 * were read — saying "this window" there would silently drop what the first
 * page disclosed while its rows were still on screen.
 *
 * `floor` is the case the server's own contract creates: a page with nothing
 * visible discloses nothing, *including* whether it withheld anything. Where
 * one of those has been read, the number is a floor and has to be told as one
 * rather than rounded up into a total nobody gave.
 */
function Withheld({
  count,
  title,
  where,
  floor,
}: {
  count: number;
  title: string;
  where: string;
  floor?: boolean;
}) {
  if (count === 0) return null;
  return (
    <Banner tone="wait" title={title}>
      {count} additional record{count === 1 ? " is" : "s are"} {where} and not visible under your
      current authorization. That is a statement about what was returned, not a count of everything
      that exists. What they are is deliberately not shown — describing withheld evidence would
      disclose the thing withholding it was for.
      {floor
        ? " A page that showed nothing disclosed nothing, including whether it withheld anything, so this is a floor rather than a total."
        : null}
    </Banner>
  );
}

function Record({ record }: { record: AuditRecord }) {
  return (
    <li>
      <span
        className={`nx-timeline__marker nx-pill--${outcomeTone(record.outcome)}`}
        aria-hidden="true"
      >
        <span className="nx-dot" />
      </span>
      <span>
        <span style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
          <strong>{record.operation ?? record.event_type}</strong>
          <Pill tone={outcomeTone(record.outcome)}>{record.outcome}</Pill>
          {record.classification ? <Classification value={record.classification} /> : null}
          <span className="nx-hint">{when(record.occurred_at)}</span>
        </span>
        <Facts>
          <Fact label="Actor">
            {record.actor_id ? <Identifier value={record.actor_id} /> : "—"}
            <span className="nx-hint"> · {record.actor_type}</span>
          </Fact>
          {record.resource_type ? (
            <Fact label="Resource">
              <Mono>{record.resource_type}</Mono>
              {record.resource_id ? (
                <span>
                  {" "}
                  <Identifier value={record.resource_id} />
                </span>
              ) : null}
            </Fact>
          ) : null}
          {record.purpose ? <Fact label="Purpose">{record.purpose}</Fact> : null}
          {record.reason_codes.length > 0 ? (
            <Fact label="Reason">{record.reason_codes.join(", ")}</Fact>
          ) : null}
          {record.policy_refs.length > 0 ? (
            <Fact label="Policy">{record.policy_refs.join(", ")}</Fact>
          ) : null}
          {record.authorization_decision_id ? (
            <Fact label="Decision">
              <Identifier value={record.authorization_decision_id} />
            </Fact>
          ) : null}
          {record.source_record_ref ? (
            <Fact label="Evidence">
              <Mono>{record.source_record_ref}</Mono>
            </Fact>
          ) : null}
          <Fact label="Audit event">
            <Identifier value={record.audit_event_id} />
          </Fact>
        </Facts>
      </span>
    </li>
  );
}

/**
 * One request, reconstructed from the record, in the order it happened.
 *
 * Not paged, and deliberately so: `/audit/correlations/{id}` takes neither a
 * limit nor a cursor. The service answers one bounded window of two hundred
 * and there is no second page to ask for, so the bound is stated and no
 * control is offered — offering one that could not work would be worse than
 * offering none.
 */
export function AuditCorrelation() {
  const { correlationId = "" } = useParams();
  const read = useCallback(
    (signal: AbortSignal) =>
      api.get<AuditResult>(`/audit/correlations/${encodeURIComponent(correlationId)}`, { signal }),
    [correlationId],
  );
  const { state, reload } = useResource<AuditResult>(read, { enabled: correlationId !== "" });

  return (
    <>
      <p className="nx-hint" style={{ marginTop: 0 }}>
        Correlation <Mono>{correlationId}</Mono>
      </p>
      <Show resource={state} what="this correlation" onRetry={reload}>
        {(result) => (
          <>
            <Withheld
              count={result.completeness === "complete" ? 0 : result.withheld}
              title="This result window is partial."
              where="in this window"
            />
            <Card
              title={
                <>
                  <LowerBound count={result.items.length} hasMore={result.has_more} /> records,
                  oldest first
                </>
              }
            >
              <ul className="nx-timeline">
                {result.items.map((record) => (
                  <Record key={record.audit_event_id} record={record} />
                ))}
              </ul>
              {result.has_more ? (
                <p className="nx-hint">
                  Showing the first {result.items.length}. A correlation this long is unusual.
                </p>
              ) : null}
            </Card>
          </>
        )}
      </Show>
    </>
  );
}

/** Search the trail. Filters narrow what the caller may already see. */
export function AuditExplorer() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    correlation_id: "",
    actor_id: "",
    resource_type: "",
    resource_id: "",
    operation: "",
  });
  const [criteria, setCriteria] = useState(form);
  // Which search the accumulated pages belong to. A count rather than the
  // criteria themselves, because asking the same question again is a new
  // answer — not more of the last one.
  const [asked, setAsked] = useState(0);

  /**
   * What each window that came back said it was short of, kept per window.
   *
   * `withheld` describes **the window that was returned**, which is the whole
   * reason it cannot simply be read off the page in hand: after a second page
   * the first page's rows are still on screen, and its disclosure has to be
   * too. The windows are a keyset walk and do not overlap — each continues
   * strictly after the last row the one before it scanned — so adding them
   * double-counts nothing.
   *
   * What the addition does not produce is a total. A page that came back
   * showing nothing disclosed nothing, including that it withheld anything, so
   * where one of those has been read the sum is a floor and is told as one.
   */
  const [disclosed, setDisclosed] = useState<
    Record<string, { withheld: number; silent: boolean }>
  >({});

  /**
   * One page of the trail.
   *
   * `AuditResult` **is** a `Page` — it is declared as one on the server and
   * extends it in the types here — so `has_more` and `next_cursor` are the
   * platform's own answer about whether another page exists rather than two
   * fields that happen to be named that way. The service sets `has_more` from
   * the scan and mints the cursor from the last row it *scanned*, not the last
   * row it showed, which is exactly why a window the caller may read none of
   * still continues rather than stranding readable evidence behind it. So
   * nothing has to be mapped here; what has to be kept is what the shared page
   * shape does not carry, and that is done above.
   */
  const readPage = useCallback(
    async (cursor: string | null, signal: AbortSignal): Promise<AuditResult> => {
      const answer = await api.get<AuditResult>("/audit/events", {
        params: { ...criteria, limit: PAGE, cursor },
        signal,
      });
      setDisclosed((seen) => ({
        // A read from the beginning is a new answer, not more of the old one.
        ...(cursor === null ? {} : seen),
        [cursor ?? ""]: {
          withheld: answer.completeness === "complete" ? 0 : answer.withheld,
          silent: answer.items.length === 0,
        },
      }));
      return answer;
    },
    [criteria],
  );
  const collection = usePagedCollection<AuditRecord>(readPage, { resetKey: String(asked) });

  const windows = Object.values(disclosed);
  const withheld = windows.reduce((total, window) => total + window.withheld, 0);
  const silent = windows.some((window) => window.silent);
  // Until the platform has answered once, there is no count to state — and
  // "0 records" while a read is in flight would be a number nobody gave.
  const answered = collection.state.kind === "ready" || collection.items.length > 0;
  const nothing = collection.items.length === 0;

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <Card title="Look for">
        <form
          style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
          onSubmit={(event) => {
            event.preventDefault();
            setCriteria({ ...form });
            setAsked((times) => times + 1);
          }}
        >
          {(
            [
              ["correlation_id", "Correlation"],
              ["actor_id", "Actor"],
              ["resource_type", "Resource type"],
              ["resource_id", "Resource"],
              ["operation", "Operation"],
            ] as const
          ).map(([key, label]) => (
            <div className="nx-field" key={key} style={{ margin: 0 }}>
              <label htmlFor={`audit-${key}`}>{label}</label>
              <input
                id={`audit-${key}`}
                className="nx-input"
                value={form[key]}
                onChange={(event) => {
                  setForm({ ...form, [key]: event.target.value });
                }}
              />
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <button type="submit" className="nx-button nx-button--primary">
              Search
            </button>
            <button type="button" className="nx-button" onClick={collection.refresh}>
              Refresh
            </button>
          </div>
        </form>
        <p className="nx-hint" style={{ marginTop: 10 }}>
          Every filter narrows what you may already see. An identifier from another tenant matches
          nothing here, and therefore says nothing about whether it exists.
        </p>
      </Card>

      {/* The server's own rule, mirrored here: a result with nothing visible
          discloses nothing, including that anything was withheld. It matters
          at the screen as well as at the route, because a read that failed or
          was refused leaves the pages that came before it with no rows on
          screen — and a count of what a result is short of, above no result,
          is a disclosure attached to nothing. */}
      <Withheld
        count={nothing ? 0 : withheld}
        title="This result is partial."
        where="in the pages read so far"
        floor={silent}
      />
      <Card
        title={
          answered ? (
            <>
              <LowerBound count={collection.items.length} hasMore={collection.hasMore} /> records,
              newest first
            </>
          ) : undefined
        }
        bodyless
      >
        <Show
          resource={collection.state}
          what="the audit trail"
          onRetry={collection.refresh}
          empty={() =>
            nothing ? (
              <Empty
                title={collection.hasMore ? "Nothing so far." : "Nothing matches."}
                hint={
                  collection.hasMore
                    ? "The trail has been read this far without anything visible to you, and it has not been read to the end."
                    : "Either nothing was recorded for this, or none of what was is visible to you."
                }
              />
            ) : null
          }
        >
          {() => (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <caption className="nx-visually-hidden">Audit records</caption>
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">What</th>
                    <th scope="col">Actor</th>
                    <th scope="col">Resource</th>
                    <th scope="col">Outcome</th>
                    <th scope="col">Correlation</th>
                  </tr>
                </thead>
                <tbody>
                  {collection.items.map((record) => (
                    <tr key={record.audit_event_id}>
                      <td>{when(record.occurred_at)}</td>
                      <td>
                        <span className="nx-table__primary">
                          {record.operation ?? record.event_type}
                        </span>
                        <span className="nx-table__secondary" style={{ display: "block" }}>
                          {record.event_type}
                        </span>
                      </td>
                      <td>{record.actor_id ? <Identifier value={record.actor_id} /> : "—"}</td>
                      <td>{record.resource_type ? <Mono>{record.resource_type}</Mono> : "—"}</td>
                      <td>
                        <Pill tone={outcomeTone(record.outcome)}>{record.outcome}</Pill>
                        {record.classification ? (
                          <Classification value={record.classification} />
                        ) : null}
                      </td>
                      <td>
                        {record.correlation_id ? (
                          <button
                            type="button"
                            className="nx-rowbutton"
                            onClick={() => {
                              void navigate(
                                `/operations/audit/${encodeURIComponent(record.correlation_id ?? "")}`,
                              );
                            }}
                          >
                            <Identifier value={record.correlation_id} />
                          </button>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Show>
        <More
          collection={collection}
          label="Show more records"
          hint={
            nothing
              ? "Nothing in the trail read so far is visible to you, and the read has not reached the end. There may be records further back that you may read."
              : "This is one page of the trail, not all of it."
          }
          placement="bodyless"
        />
      </Card>
    </div>
  );
}
